import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import AppNavBar from './AppNavBar';

const AssessDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const ADMIN_EMAILS = [
    'thomasfairgrieve3@gmail.com',
    'adriandalzell@gmail.com',
    'mark.gomersall@newdur.ac.uk',
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      
      if (ADMIN_EMAILS.includes(user.email)) {
        setLoading(false);
        return;
      }
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const isSubscribed = data?.subscriptionStatus === 'active' ||
            data?.subscriptionTier === 'premium' ||
            data?.subscriptionTier === 'premiumPlus';
          
          if (!isSubscribed) {
            navigate('/upgrade');
            return;
          }
        } else {
          navigate('/upgrade');
          return;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sand-100 to-sand-200">
        <div className="text-ink-900 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="dashboard" />
      
      <div className="flex items-center justify-center py-12">
        <div className="max-w-5xl mx-auto px-4">

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-coral-500 leading-tight mb-8 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              Welcome to TeAIch Assess
            </h1>
            <p className="text-xl text-ink-700">Choose an action to get started</p>
          </div>

          {/* Two Large Action Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            
            {/* Mark Work Card */}
            <div 
              onClick={() => navigate('/mark-work')}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-brutal-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group flex flex-col h-full"
            >
              <div className="p-12 text-center flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-400 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold text-ink-800 mb-4 group-hover:text-coral-600 transition-colors">
                    Mark a Piece of Work
                  </h2>
                  <p className="text-ink-600 text-lg leading-relaxed mb-8">
                    Upload student work and get AI-powered marking with detailed feedback and suggestions for improvement.
                  </p>
                </div>
                <div className="mt-auto">
                  <div className="bg-coral-300 text-white px-8 py-4 rounded-lg font-semibold text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block">
                    Start Marking →
                  </div>
                </div>
              </div>
            </div>

            {/* Mark Schemes Card */}
            <div 
              onClick={() => navigate('/my-sol')}
              className="bg-white rounded-lg border-4 border-ink-900 shadow-brutal hover:shadow-brutal-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group flex flex-col h-full"
            >
              <div className="p-12 text-center flex flex-col flex-grow">
                <div className="mb-8">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-coral-300 to-coral-400 rounded-full flex items-center justify-center group-hover:from-coral-500 group-hover:to-coral-600 transition-all duration-300">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold text-ink-800 mb-4 group-hover:text-coral-600 transition-colors">
                    Add & View Mark Schemes
                  </h2>
                  <p className="text-ink-600 text-lg leading-relaxed mb-8">
                    Create, manage, and view your marking schemes and success criteria for consistent assessment.
                  </p>
                </div>
                <div className="mt-auto">
                  <div className="bg-coral-300 text-white px-8 py-4 rounded-lg font-semibold text-lg group-hover:bg-coral-500 transition-all duration-300 inline-block">
                    Manage Schemes →
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AssessDashboard;